import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'

import noop from 'lib/function/noop'
import GeoContext from 'contexts/geoContext'
import { useGetChat } from 'tripPlanner/hooks/api/chat'
import type { ChatNavigationOptions } from '../hooks/useChatNavigation'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { DrawerAskAIProvider } from '../contexts/DrawerAskAIContext'
import { MapInteractionProvider } from '../contexts/MapInteractionContext'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useTripPlannerMenuDrawer } from '../hooks/useTripPlannerMenuDrawer'
import TripChatMobileAppShell from '../mobile/TripChatMobileAppShell'
import TripChatMobileChatView from '../mobile/TripChatMobileChatView'
import TripChatMobileTimelineView from '../mobile/TripChatMobileTimelineView'
import TripChatMobileListView from '../mobile/TripChatMobileListView'
import TripChatMobileMapView from '../mobile/TripChatMobileMapView'
import TripChatMobileCartPanelHost from '../mobile/TripChatMobileCartPanelHost'
import DrawerContainer from '../containers/DrawerContainer'
import { useTripChatMobileOverlay } from '../mobile/TripChatMobileOverlayContext'
import { useMessageContext } from '../hooks/useMessageContext'
import { useSpoofSendGuard } from 'tripPlanner/hooks/useSpoofSendGuard'
import { useChatSessionTracking } from '../hooks/useChatSessionTracking'
import { usePromptTokenSubmission } from '../hooks/usePromptTokenSubmission'
import { useChatRouteSync } from '../hooks/useChatRouteSync'
import { useCartButtonSummary } from '../hooks/useCartButtonSummary'
import {
  type TripChatMobileCurrentTripActionsSheetCloseSource,
  useTripChatMobileCurrentTripActionsSheet,
} from '../hooks/useTripChatMobileCurrentTripActionsSheet'
import { useTripListPricingContext } from '../contexts/TripListPricingContext'
import { useFetchCartPanelOffers } from '../hooks/useFetchCartPanelOffers'
import { addBookingItemIdsToSelection, type BookingSelection } from '../utils/bookingSelection'
import ChatLandingPage from '../../ChatLandingPage/ChatLandingPage'
import { useAssistantHistory } from 'tripPlanner/assistant/hooks/useAssistantHistory'
import { formatChatLabel } from 'tripPlanner/assistant/utils/format'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'

export interface TripChatMobileSurfaceContentProps {
  chatId: number | undefined
  syncChatId: (id: number | undefined, options?: ChatNavigationOptions) => void
  isNewChatRoute: boolean
  isLandingRoute: boolean
  existingRouteChatId: number | undefined
  navigateToNewChatRoute: () => void
  isLoggedIn: boolean
  isStaff: boolean
  isDevMode: boolean
  isSpoofed: boolean
  spoofingAgentEmail?: string
  onStaffViewedChatIdChange: (chatId: number | undefined) => void
  onDevModeToggle: () => void
  setBookingSelection: Dispatch<SetStateAction<BookingSelection>>
  selectedBookingItemIds?: Array<string>
  prefilledOrderChipLabels: Array<string>
  onRemovePrefilledOrderTag: (tag: string) => void
}

const DEFAULT_SELECTED_BOOKING_ITEM_IDS: Array<string> = []

export default function TripChatMobileSurfaceContent({
  chatId,
  syncChatId,
  isNewChatRoute,
  isLandingRoute,
  existingRouteChatId,
  navigateToNewChatRoute,
  isLoggedIn,
  isStaff,
  isDevMode,
  isSpoofed,
  spoofingAgentEmail,
  onStaffViewedChatIdChange,
  onDevModeToggle,
  setBookingSelection,
  selectedBookingItemIds = DEFAULT_SELECTED_BOOKING_ITEM_IDS,
  prefilledOrderChipLabels,
  onRemovePrefilledOrderTag,
}: TripChatMobileSurfaceContentProps) {
  const intl = useIntl()
  const {
    actionsCloseRequest,
    cartCloseRequest,
    closeOverlay,
    drawerCloseRequest,
    menuCloseRequest,
    openOverlay,
  } = useTripChatMobileOverlay()
  const eventBus = useTripPlannerEventBus()
  const { hasItineraryData, itineraryData } = useItineraryContext()
  const { luxPlusEnabled } = useTripListPricingContext()
  useFetchCartPanelOffers(itineraryData, !!itineraryData)
  const cartButtonSummary = useCartButtonSummary(itineraryData, isLoggedIn, luxPlusEnabled)
  const {
    recentChats,
    refetchChatList,
    updateChatListTitle,
  } = useAssistantHistory({ enabled: isLoggedIn, isLoggedIn })
  const { search } = useLocation()
  const { submitMessage, resetConversation, resetStreamingState, setFollowTail } = useChatActions()
  const { messages } = useChatStreamMessages()
  const { isStreaming, loadingState } = useChatStreamStatus()
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null)
  const [hoveredHotelId, setHoveredHotelId] = useState<string | undefined>(undefined)
  const isCardHoveredRef = useRef(false)
  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'
  const trackFirstMessage = useChatSessionTracking({ chatId, existingRouteChatId })
  const { currentRegionCode } = useContext(GeoContext)
  const { data: chatData } = useGetChat(chatId, currentRegionCode)
  const hiddenFromCustomer = chatData?.hiddenFromCustomer ?? true

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus()
  }, [])

  const handleMarkerHover = useCallback((markerId: string | undefined) => {
    if (markerId === undefined && isCardHoveredRef.current) return
    setHoveredHotelId(markerId)
  }, [])

  const handleCardHover = useCallback((offerId: string | undefined) => {
    isCardHoveredRef.current = offerId !== undefined
    setHoveredHotelId(offerId)
  }, [])

  const mapInteractionValue = useMemo(() => ({
    onCardHover: handleCardHover,
    onMarkerHover: handleMarkerHover,
    highlightedMarkerId: hoveredHotelId,
  }), [handleCardHover, handleMarkerHover, hoveredHotelId])

  const {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    submitDirect,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    resetSelectionState,
  } = useMessageContext({
    streamingSubmit: submitMessage,
    setFollowTail,
    setMessageInputValue: noop,
    focusChatInput,
    prefilledMultiChoiceSelection: prefilledOrderChipLabels,
  })

  const { confirmOrAbort } = useSpoofSendGuard({ isSpoofed, hiddenFromCustomer, chatId })

  const handleSubmitWithTracking = useCallback(async(message: string) => {
    if (!(await confirmOrAbort())) return
    trackFirstMessage()
    return handleSubmit(message)
  }, [confirmOrAbort, handleSubmit, trackFirstMessage])

  const submitDirectWithTracking = useCallback(async(message: string) => {
    if (!(await confirmOrAbort())) return
    trackFirstMessage()
    return submitDirect(message)
  }, [confirmOrAbort, submitDirect, trackFirstMessage])

  const resetChatSpecificState = useCallback(() => {
    eventBus.dispatch({ type: 'reset_chat_state' })
    resetSelectionState()
    setHoveredHotelId(undefined)
    isCardHoveredRef.current = false
  }, [eventBus, resetSelectionState])

  const resetConversationState = useCallback((options?: ChatNavigationOptions) => {
    syncChatId(undefined, { shouldUpdateUrl: options?.shouldUpdateUrl })
    resetConversation({ followTail: false })
    resetChatSpecificState()
  }, [resetChatSpecificState, resetConversation, syncChatId])

  const loadChatById = useCallback((selectedChatId: number, options?: ChatNavigationOptions) => {
    if (selectedChatId === chatId) {
      return
    }

    resetChatSpecificState()
    resetStreamingState()
    syncChatId(selectedChatId, options)
    setFollowTail(false)
  }, [chatId, resetChatSpecificState, resetStreamingState, setFollowTail, syncChatId])

  const handleStartNewChat = useCallback(() => {
    onStaffViewedChatIdChange(undefined)
    resetConversationState({ shouldUpdateUrl: false })
    navigateToNewChatRoute()
  }, [navigateToNewChatRoute, onStaffViewedChatIdChange, resetConversationState])

  const handleChatSelect = useCallback((selectedChatId: number) => {
    onStaffViewedChatIdChange(undefined)
    loadChatById(selectedChatId)
  }, [loadChatById, onStaffViewedChatIdChange])

  const handleChatDeleted = useCallback((deletedChatId: number, fallbackChatId: number | undefined) => {
    if (chatId !== deletedChatId) {
      return
    }

    onStaffViewedChatIdChange(undefined)
    if (fallbackChatId) {
      loadChatById(fallbackChatId)
      return
    }

    resetConversationState()
  }, [chatId, loadChatById, onStaffViewedChatIdChange, resetConversationState])

  useChatRouteSync({
    chatId,
    existingRouteChatId,
    isLandingRoute,
    isNewChatRoute,
    loadChatById,
    resetConversationState,
  })

  usePromptTokenSubmission({
    search,
    isNewChatRoute,
    isLoading,
    isStreaming,
    onPromptSubmit: handleSubmitWithTracking,
  })

  const handleBookingItemsAdded = useCallback((bookingItemIds: Array<string>) => {
    if (bookingItemIds.length === 0) {
      return
    }

    setBookingSelection(previous => addBookingItemIdsToSelection(previous, bookingItemIds))
  }, [setBookingSelection])

  const landingPageTags = useMemo(() => {
    return [...new Set([...prefilledOrderChipLabels, ...activeMultiChoiceSelection])]
  }, [activeMultiChoiceSelection, prefilledOrderChipLabels])

  const handleAddTags = useCallback((tags: Array<string>) => {
    const selectedTags = new Set(landingPageTags)
    const deduplicated = tags.filter(tag => !selectedTags.has(tag))
    if (deduplicated.length > 0) {
      handleMultiChoiceSelectionChange([...activeMultiChoiceSelection, ...deduplicated])
    }
  }, [activeMultiChoiceSelection, handleMultiChoiceSelectionChange, landingPageTags])

  const handleLandingPageTagRemove = useCallback((tag: string) => {
    if (prefilledOrderChipLabels.includes(tag)) {
      onRemovePrefilledOrderTag(tag)
      return
    }

    handleRemoveTag(tag)
  }, [handleRemoveTag, onRemovePrefilledOrderTag, prefilledOrderChipLabels])

  const currentChat = useMemo(() => {
    return recentChats.find(chat => chat.id === chatId)
  }, [chatId, recentChats])

  const currentTripPersistedTitle = currentChat?.name?.trim()

  const currentTripTitle = useMemo(() => {
    const chatName = currentChat?.name?.trim()
    if (chatName) {
      return chatName
    }

    if (currentChat) {
      return formatChatLabel(getChatLastActivityTimestamp(currentChat), intl)
    }

    const stopNames = itineraryData?.stops
      .map(stop => stop.placeName.trim())
      .filter(Boolean)
    if (stopNames?.length) {
      return stopNames.join(' + ')
    }

    return intl.formatMessage({
      id: 'uW8GpDIKoGJ7OdO8DdNE',
      defaultMessage: 'Current trip',
      description: 'Fallback title for the mobile current TripPlanner itinerary actions sheet',
    })
  }, [currentChat, intl, itineraryData?.stops])

  const handleCurrentTripRenameSuccess = useCallback((name: string) => {
    if (!chatId) return

    updateChatListTitle(chatId, name)
    refetchChatList().catch(noop)
  }, [chatId, refetchChatList, updateChatListTitle])

  const handleMenuOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      openOverlay('menu')
      return
    }

    closeOverlay('menu', {
      shouldRequestOverlayClose: false,
      shouldStepHistory: false,
    })
  }, [closeOverlay, openOverlay])

  const handleCurrentTripActionsOpenChange = useCallback((
    isOpen: boolean,
    closeSource: TripChatMobileCurrentTripActionsSheetCloseSource = 'ui',
  ) => {
    if (isOpen) {
      openOverlay('actions')
      return
    }

    closeOverlay('actions', {
      shouldRequestOverlayClose: false,
      shouldStepHistory: closeSource === 'ui',
    })
  }, [closeOverlay, openOverlay])

  const onCurrentTripActionsOpen = useTripChatMobileCurrentTripActionsSheet({
    chatId,
    closeSignal: actionsCloseRequest,
    onRenameSuccess: handleCurrentTripRenameSuccess,
    persistedTitle: currentTripPersistedTitle,
    title: currentTripTitle,
    onSheetOpenChange: handleCurrentTripActionsOpenChange,
  })

  const onMenuOpen = useTripPlannerMenuDrawer({
    variant: 'mobile',
    isLoggedIn,
    isStaff,
    isDevMode,
    onStartNewChat: handleStartNewChat,
    onChatSelect: handleChatSelect,
    onChatDeleted: handleChatDeleted,
    onDevModeToggle,
    closeSignal: menuCloseRequest,
    onDrawerOpenChange: handleMenuOpenChange,
  })

  const handleCartOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      openOverlay('cart')
      return
    }

    closeOverlay('cart', {
      shouldRequestOverlayClose: false,
      shouldStepHistory: false,
    })
  }, [closeOverlay, openOverlay])

  const handleDrawerOpenChange = useCallback((isOpen: boolean) => {
    if (isOpen) {
      openOverlay('drawer')
      return
    }

    closeOverlay('drawer', {
      shouldRequestOverlayClose: false,
      shouldStepHistory: false,
    })
  }, [closeOverlay, openOverlay])

  const hasMessages = messages.length > 0
  const shouldShowLandingPage = useMemo(() => {
    const isLandingExperience = isLandingRoute && !hasMessages
    const shouldShowNewChatComposer = isNewChatRoute && !hasMessages

    return isLandingExperience || shouldShowNewChatComposer
  }, [hasMessages, isLandingRoute, isNewChatRoute])

  const tabPanels = useMemo(() => ({
    chat: shouldShowLandingPage ? (
      <ChatLandingPage
        onSubmit={handleSubmitWithTracking}
        onSubmitDirect={submitDirectWithTracking}
        onAddTags={handleAddTags}
        disabled={isLoading || isStreaming}
        selectedTags={landingPageTags}
        onRemoveTag={handleLandingPageTagRemove}
        onBookingSelectionChange={setBookingSelection}
        onBookingItemsAdded={handleBookingItemsAdded}
      />
    ) : (
      <TripChatMobileChatView
        chatId={chatId}
        isLoggedIn={isLoggedIn}
        isDevMode={isDevMode}
        isSpoofed={isSpoofed}
        hiddenFromCustomer={hiddenFromCustomer}
        spoofingAgentEmail={spoofingAgentEmail}
        navigateToNewChatRoute={navigateToNewChatRoute}
        selectedBookingItemIds={selectedBookingItemIds}
        onBookingItemsAdded={handleBookingItemsAdded}
        handleSubmit={handleSubmitWithTracking}
        handleMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
        activeMultiChoiceSelection={activeMultiChoiceSelection}
        prefilledSelectionTags={prefilledOrderChipLabels}
        mobileSelectionText={mobileSelectionText}
        offerContextTag={offerContextTag}
        handleRemoveTag={handleRemoveTag}
        onRemovePrefilledSelectionTag={onRemovePrefilledOrderTag}
        chatInputRef={chatInputRef}
      />
    ),
    timeline: <TripChatMobileTimelineView />,
    list: <TripChatMobileListView />,
    map: <TripChatMobileMapView />,
  }), [
    activeMultiChoiceSelection,
    chatId,
    handleAddTags,
    handleBookingItemsAdded,
    handleLandingPageTagRemove,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    handleSubmitWithTracking,
    isDevMode,
    isLoading,
    isLoggedIn,
    isSpoofed,
    hiddenFromCustomer,
    isStreaming,
    landingPageTags,
    mobileSelectionText,
    navigateToNewChatRoute,
    offerContextTag,
    onRemovePrefilledOrderTag,
    prefilledOrderChipLabels,
    selectedBookingItemIds,
    setBookingSelection,
    shouldShowLandingPage,
    spoofingAgentEmail,
    submitDirectWithTracking,
  ])

  return (
    <MapInteractionProvider value={mapInteractionValue}>
      <DrawerAskAIProvider
        submitMessage={submitMessage}
        messages={messages}
        isStreaming={isStreaming}
      >
        <TripChatMobileAppShell
          onMenuOpen={onMenuOpen}
          onCurrentTripActionsOpen={hasItineraryData ? onCurrentTripActionsOpen : undefined}
          tabPanels={tabPanels}
          showTabs={!shouldShowLandingPage}
          showComposerReservedSpace={false}
          showCartButton={!!itineraryData}
          cartItemCount={cartButtonSummary.bookableItemCount}
          cartTotalPrice={cartButtonSummary.estimatedTotal}
          isCartButtonLoading={cartButtonSummary.isLoading}
          isSpoofed={isSpoofed}
          hiddenFromCustomer={hiddenFromCustomer}
          chatExists={chatId !== undefined}
          chatId={chatId}
          onCreateNewChat={handleStartNewChat}
        >
          <DrawerContainer
            closeSignal={drawerCloseRequest}
            onDrawerOpenChange={handleDrawerOpenChange}
          />
          <TripChatMobileCartPanelHost
            closeSignal={cartCloseRequest}
            onCartOpenChange={handleCartOpenChange}
          />
        </TripChatMobileAppShell>
      </DrawerAskAIProvider>
    </MapInteractionProvider>
  )
}
